import { useState } from 'react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import {
  MaintenanceRecordWithDisplayName,
  MaintenanceReminderWithDisplayNameVIN,
  PresignedUrlObject,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { getFileExtension } from '../../../../../../../utils/FileUtils';
import {
  MaintenanceAsset,
  MaintenanceFormData,
  MaintenanceKind,
  MaintenanceMode,
} from './types';

// Backend validation messages are prefixed with the HTTP reason, e.g.
// "Bad Request: Record already has an active maintenance reminder." — strip
// that prefix so the user sees the human-readable part.
const BACKEND_ERROR_PREFIX = /^(bad request|conflict|error|not found):\s*/i;

type UseMaintenanceMutationsArgs = {
  kind: MaintenanceKind;
  mode: MaintenanceMode;
  selectedAsset?: MaintenanceAsset;
  existingRecord?: MaintenanceRecordWithDisplayName;
  existingReminder?: MaintenanceReminderWithDisplayNameVIN;
  pickedAsset?: string;
  file: File | null;
  fileName: string | null;
  isNewFile: boolean;
  onClose: () => void;
  onRecordChange: () => void;
  resetForm: () => void;
  clearFile: () => void;
};

/**
 * Picks the endpoint (`/maintenanceRecords` vs `/maintenanceReminders`) and
 * method (POST create / PATCH edit) from the discriminant and submits the
 * same payloads, toasts, and refresh calls as the four legacy modals —
 * including the deliberately-different reminder payloads (the record-create
 * path historically sent `remind_at`, every other path sends
 * `mileage`/`days`/`date`).
 *
 * Success is detected by HTTP 2xx status: these endpoints return 200 on update
 * and 201 on create, so checking either avoids misreading a successful edit as
 * a failure. Error UX: every call passes `hideAlert` so the generic
 * AxiosWrapper toast is suppressed, and on failure we surface the specific
 * backend message (e.g. "Record already has an active maintenance reminder.")
 * via `toastBackendError`.
 */
export function useMaintenanceMutations({
  kind,
  mode,
  selectedAsset,
  existingRecord,
  existingReminder,
  pickedAsset,
  file,
  fileName,
  isNewFile,
  onClose,
  onRecordChange,
  resetForm,
  clearFile,
}: UseMaintenanceMutationsArgs) {
  const { callProtectedApi, callApi } = useApi();
  const [loading, setLoading] = useState(false);

  const succeeded = (response: AxiosResponse<any> | null) =>
    response?.status === HttpStatusCode.Ok ||
    response?.status === HttpStatusCode.Created;

  const toastBackendError = (
    response: AxiosResponse<any> | null,
    fallback: string
  ) => {
    const raw =
      typeof response?.data?.message === 'string'
        ? response.data.message
        : undefined;
    const cleaned = raw?.replace(BACKEND_ERROR_PREFIX, '').trim();
    toast.error(cleaned && cleaned.length > 0 ? cleaned : fallback);
  };

  const uploadMaintenanceFile = async (f: File, url: PresignedUrlObject) => {
    callApi(
      '',
      'put',
      f,
      'Error occurred while uploading maintenance record.',
      { 'Content-Type': f.type },
      url?.presignedUrl,
      5
    ).then((result) => {
      if (result?.status !== HttpStatusCode.Ok) {
        toast.error('Failed to upload maintenance record.');
      }
    });
  };

  // ---- record / create: reminder sub-call (legacy used `remind_at`) ----
  const createReminderForNewRecord = async (
    record_id: string,
    recordInfo: MaintenanceFormData
  ) => {
    try {
      const response = await callProtectedApi(
        '/maintenanceReminders',
        'post',
        {
          asset_id: selectedAsset?.truck_id || selectedAsset?.trailer_id,
          asset_type: selectedAsset?.asset_type,
          reminder_type: recordInfo.reminderType,
          record_id: record_id,
          remind_at:
            recordInfo.reminderType === 'date'
              ? new Date(recordInfo?.reminderValue ?? '')
              : recordInfo?.reminderValue,
          maintenance_type: recordInfo.maintenanceType,
        },
        'Error occurred while creating your maintenance reminder.',
        undefined,
        undefined,
        0,
        true
      );
      if (succeeded(response)) {
        onRecordChange();
        return true;
      }
      toastBackendError(
        response,
        'Error occurred while creating your maintenance reminder.'
      );
      return false;
    } catch (e: any) {
      console.error(
        'Error occurred while creating your maintenance reminder: ',
        e
      );
      return false;
    }
  };

  const createMaintenanceRecord = async (recordInfo: MaintenanceFormData) => {
    setLoading(true);
    try {
      let fileExtension = '';
      if (file) {
        fileExtension = getFileExtension(file) ?? '';
      }
      const response = await callProtectedApi(
        '/maintenanceRecords',
        'post',
        {
          asset_id: selectedAsset?.truck_id || selectedAsset?.trailer_id,
          asset_type: selectedAsset?.asset_type,
          maintenance_type: recordInfo.maintenanceType,
          description: recordInfo?.description,
          date_of_maintenance: new Date(recordInfo?.dateOfMaintenance ?? ''),
          file_extension: fileExtension,
          // The /maintenanceRecords POST validator requires isNewFile. A file
          // is always required on create, so a new file is always uploaded.
          isNewFile: true,
        },
        'Error occurred while creating your maintenance record.',
        undefined,
        undefined,
        0,
        true
      );
      const data = response?.data?.data;
      if (succeeded(response)) {
        if (file) {
          await uploadMaintenanceFile(file, data?.presigned_url);
        }
        if (recordInfo.setReminder) {
          const found = await createReminderForNewRecord(
            data?.maintenance_record?.record_id,
            recordInfo
          );
          if (!found) {
            // createReminderForNewRecord already surfaced the backend reason;
            // throw so the "record created" success toast does not fire.
            throw new Error(
              'Error occurred while creating your maintenance reminder.'
            );
          }
        }
        resetForm();
        clearFile();
        onClose();
        onRecordChange();
        toast.success('Maintenance record created successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while creating your maintenance record.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your maintenance record: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  // ---- record / edit: reminder sub-calls (legacy used mileage/days/date) ----
  const createReminderForEditedRecord = async (
    recordInfo: MaintenanceFormData
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/maintenanceReminders',
        'post',
        {
          asset_id: selectedAsset?.truck_id || selectedAsset?.trailer_id,
          asset_type: selectedAsset?.asset_type,
          reminder_type: recordInfo?.reminderType,
          record_id: existingRecord?.record_id,
          mileage:
            recordInfo?.reminderType === 'mileage'
              ? parseInt(recordInfo?.reminderValue ?? '')
              : undefined,
          days:
            recordInfo?.reminderType === 'days'
              ? parseInt(recordInfo?.reminderValue ?? '')
              : undefined,
          date:
            recordInfo?.reminderType === 'date'
              ? new Date(recordInfo?.reminderValue ?? '')
              : undefined,
          maintenance_type: recordInfo?.maintenanceType,
        },
        'Error occurred while creating your maintenance reminder.',
        undefined,
        undefined,
        0,
        true
      );
      if (succeeded(response)) {
        onRecordChange();
        toast.success('Maintenance reminder created successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while creating your maintenance reminder.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your maintenance reminder: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const updateReminderForEditedRecord = async (
    recordInfo: MaintenanceFormData
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/maintenanceReminders/update/${existingRecord?.reminder?.reminder_id}`,
        'patch',
        {
          asset_id: selectedAsset?.truck_id || selectedAsset?.trailer_id,
          asset_type: selectedAsset?.asset_type,
          reminder_type: recordInfo?.reminderType,
          record_id: existingRecord?.record_id,
          mileage:
            recordInfo?.reminderType === 'mileage'
              ? parseInt(recordInfo?.reminderValue ?? '')
              : undefined,
          days:
            recordInfo?.reminderType === 'days'
              ? parseInt(recordInfo?.reminderValue ?? '')
              : undefined,
          date:
            recordInfo?.reminderType === 'date'
              ? new Date(recordInfo?.reminderValue ?? '')
              : undefined,
          maintenance_type: recordInfo?.maintenanceType,
        },
        'Error occurred while updating your maintenance reminder.',
        undefined,
        undefined,
        0,
        true
      );
      if (succeeded(response)) {
        onRecordChange();
        toast.success('Maintenance reminder updated successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while updating your maintenance reminder.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating your maintenance reminder: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const updateMaintenanceRecord = async (recordInfo: MaintenanceFormData) => {
    setLoading(true);
    try {
      let fileExtension = '';
      if (file) {
        fileExtension = getFileExtension(file) ?? '';
      }
      const response = await callProtectedApi(
        `/maintenanceRecords/update/${existingRecord?.record_id}`,
        'patch',
        {
          asset_id: selectedAsset?.truck_id || selectedAsset?.trailer_id,
          asset_type: selectedAsset?.asset_type,
          maintenance_type: recordInfo.maintenanceType,
          description: recordInfo?.description,
          date_of_maintenance: recordInfo?.dateOfMaintenance
            ? new Date(recordInfo.dateOfMaintenance)
            : undefined,
          file_extension: fileExtension || fileName?.split('.')?.pop(),
          isNewFile: isNewFile,
        },
        'Error occurred while updating your maintenance record.',
        undefined,
        undefined,
        0,
        true
      );
      const data = response?.data?.data;
      if (succeeded(response)) {
        if (file) {
          await uploadMaintenanceFile(file, data?.presigned_url);
        }
        if (recordInfo.setReminder && !existingRecord?.reminder?.reminder_id) {
          await createReminderForEditedRecord(recordInfo);
        } else if (
          recordInfo.setReminder &&
          existingRecord?.reminder?.reminder_id
        ) {
          await updateReminderForEditedRecord(recordInfo);
        }
        resetForm();
        clearFile();
        onClose();
        onRecordChange();
        toast.success('Maintenance record updated successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while updating your maintenance record.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating your maintenance record: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  // ---- reminder / create ----
  const createMaintenanceReminder = async (
    reminderInfo: MaintenanceFormData
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/maintenanceReminders',
        'post',
        {
          asset_id:
            selectedAsset?.truck_id ||
            selectedAsset?.trailer_id ||
            pickedAsset?.split('#')[0],
          asset_type: selectedAsset?.asset_type || pickedAsset?.split('#')[1],
          reminder_type: reminderInfo.reminderType,
          record_id:
            reminderInfo?.recordId && reminderInfo?.recordId !== ''
              ? reminderInfo?.recordId
              : undefined,
          mileage:
            reminderInfo.reminderType === 'mileage'
              ? parseInt(reminderInfo?.reminderValue ?? '')
              : undefined,
          days:
            reminderInfo.reminderType === 'days'
              ? parseInt(reminderInfo?.reminderValue ?? '')
              : undefined,
          date:
            reminderInfo.reminderType === 'date'
              ? new Date(reminderInfo?.reminderValue ?? '')
              : undefined,
          maintenance_type: reminderInfo.maintenanceType,
        },
        'Error occurred while creating your maintenance reminder.',
        undefined,
        undefined,
        0,
        true
      );
      if (succeeded(response)) {
        resetForm();
        onClose();
        onRecordChange();
        toast.success('Maintenance reminder created successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while creating your maintenance reminder.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your maintenance reminder: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  // ---- reminder / edit ----
  const updateMaintenanceReminder = async (
    reminderInfo: MaintenanceFormData
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/maintenanceReminders/update/${existingReminder?.reminder_id}`,
        'patch',
        {
          reminder_type: reminderInfo.reminderType,
          record_id: reminderInfo?.recordId,
          mileage:
            reminderInfo.reminderType === 'mileage'
              ? parseInt(reminderInfo?.reminderValue ?? '')
              : undefined,
          days:
            reminderInfo.reminderType === 'days'
              ? parseInt(reminderInfo?.reminderValue ?? '')
              : undefined,
          date:
            reminderInfo.reminderType === 'date'
              ? new Date(reminderInfo?.reminderValue ?? '')
              : undefined,
          maintenance_type: reminderInfo.maintenanceType,
        },
        'Error occurred while updating your maintenance reminder.',
        undefined,
        undefined,
        0,
        true
      );
      if (succeeded(response)) {
        resetForm();
        onClose();
        onRecordChange();
        toast.success('Maintenance reminder updated successfully!');
      } else {
        toastBackendError(
          response,
          'Error occurred while updating your maintenance reminder.'
        );
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating your maintenance reminder: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const submit = (data: MaintenanceFormData) => {
    if (kind === 'record') {
      if (mode === 'create') return createMaintenanceRecord(data);
      return updateMaintenanceRecord(data);
    }
    if (mode === 'create') return createMaintenanceReminder(data);
    return updateMaintenanceReminder(data);
  };

  return { loading, submit };
}
